import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  useTheme,
} from '@mui/material';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import PersonIcon from '@mui/icons-material/Person';
import { IZoneServiceItemLog } from 'models/zone.model';

interface CleaningLog {
  id: number;
  created_at: string;
  area: string;
  cleaner: string;
  cleaned: number;
  disinfected: number;
  sanitized: number;
  refilled: number;
  serviced: number;
  service_items?: IZoneServiceItemLog[];
}

interface CleaningHistoryTableProps {
  logs: CleaningLog[];
  onLoadMore: () => void;
  loading: boolean;
  hasMore: boolean;
}

export default function CleaningHistoryTable({ logs, onLoadMore, loading, hasMore }: CleaningHistoryTableProps) {
  const theme = useTheme();
  const servicesPerformed = (log: CleaningLog): string[] => {
    const services = [];
    if (log?.cleaned) {
      services.push('cleaned');
    }

    if (log?.disinfected) {
      services.push('disinfected');
    }

    if (log?.refilled) {
      services.push('refilled');
    }

    if (log?.sanitized) {
      services.push('sanitized');
    }

    if (log?.serviced) {
      services.push('serviced');
    }

    log?.service_items?.forEach((serviceItemLog) => {
      if (serviceItemLog.serviced && serviceItemLog.clean_code_zone_service_item) {
        services.push(serviceItemLog.clean_code_zone_service_item.name);
      }
    });

    return services;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date & Time</TableCell>
              <TableCell>Area</TableCell>
              <TableCell>Cleaner</TableCell>
              <TableCell align="right">Services Performed</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" />
                    {dayjs(log.created_at).format('hh:mm A · MM/DD/YYYY')}
                  </Box>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <PlaceIcon fontSize="small" />
                    {log.area}
                  </Box>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    {log.cleaner}
                  </Box>
                </TableCell>

                <TableCell align="right">
                  {servicesPerformed(log).map((service, index) => (
                    <Chip
                      key={index}
                      label={service}
                      variant="outlined"
                      size={'small'}
                      style={{
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        fontSize: 10,
                        marginRight: 5,
                        marginBottom: 5,
                      }}
                    />
                  ))}
                </TableCell>
              </TableRow>
            ))}

            {!loading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No cleaning history found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        {loading ? (
          <CircularProgress size={22} />
        ) : (
          <Button variant="outlined" size="small" disabled={!hasMore} onClick={onLoadMore}>
            {hasMore ? 'Load More' : 'No more records'}
          </Button>
        )}
      </Box>
    </Box>
  );
}
